import React from "react";
import "./ChatResponseRenderer.css";

const PLATFORM_NAMES = ["shopify", "ebay", "poshmark", "mercari", "depop", "etsy", "amazon"];

function normalizeText(value) {
  return (value || "")
    .replace(/\r/g, "")
    .replace(/`/g, "")
    .replace(/\u2022/g, "-")
    .trim();
}

function parseInline(text) {
  const input = normalizeText(text);
  const nodes = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(input)) !== null) {
    const before = input.slice(lastIndex, match.index).replace(/\*/g, "");
    if (before) nodes.push(before);
    nodes.push(
      <strong key={`${match.index}-${match[1]}`} className="chat-inline-strong">
        {match[1].replace(/\*/g, "")}
      </strong>
    );
    lastIndex = regex.lastIndex;
  }

  const tail = input.slice(lastIndex).replace(/\*/g, "");
  if (tail) nodes.push(tail);

  return nodes.length > 0 ? nodes : [input.replace(/\*/g, "")];
}

function parsePlatformRow(item) {
  const raw = normalizeText(item).replace(/^[-*]\s*/, "");
  const lower = raw.toLowerCase();
  const platform = PLATFORM_NAMES.find((name) => lower.includes(name));
  if (!platform) return null;

  const qtyMatch =
    raw.match(/(\d+(?:\.\d+)?)\s*(products?|listings?|items?|units?)/i) ||
    raw.match(/:\s*(\d+(?:\.\d+)?)/);
  const count = qtyMatch ? Number(qtyMatch[1]) : null;
  if (count === null || Number.isNaN(count)) return null;

  const suffixMatch = raw.match(/(products?|listings?|items?|units?)/i);
  const unit = suffixMatch ? suffixMatch[1].toLowerCase() : "products";

  return {
    platform:
      platform === "ebay"
        ? "eBay"
        : platform.charAt(0).toUpperCase() + platform.slice(1),
    count,
    unit,
  };
}

function groupBlocks(text) {
  const lines = normalizeText(text).split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listLines = [];
  let listOrdered = false;

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      const merged = paragraphLines.join(" ").trim();
      if (merged) blocks.push({ type: "paragraph", text: merged });
      paragraphLines = [];
    }
  };

  const flushList = () => {
    if (listLines.length > 0) {
      blocks.push({ type: "list", items: [...listLines], ordered: listOrdered });
      listLines = [];
      listOrdered = false;
    }
  };

  lines.forEach((line) => {
    const raw = line.trim();
    if (!raw) {
      flushParagraph();
      flushList();
      return;
    }

    const isOrdered = /^\d+\.\s+/.test(raw);
    const isBullet = /^[-*]\s+/.test(raw);

    if (isOrdered || isBullet) {
      flushParagraph();
      // If list type changes, flush and start new
      if (listLines.length > 0 && listOrdered !== isOrdered) {
        flushList();
      }
      listOrdered = isOrdered;
      listLines.push(raw.replace(/^\d+\.\s+/, "").replace(/^[-*]\s+/, "").trim());
    } else {
      flushList();
      paragraphLines.push(raw);
    }
  });

  flushParagraph();
  flushList();
  return blocks;
}

function renderPlatformCards(platformRows) {
  const sorted = [...platformRows].sort((a, b) => b.count - a.count);
  const max = sorted[0]?.count || 1;

  return (
    <div className="chat-platform-wrap">
      <div className="chat-platform-grid">
        {sorted.map((row, index) => (
          <div key={`${row.platform}-${index}`} className={`chat-platform-card ${index === 0 ? "top" : ""}`}>
            <div className="chat-platform-head">
              <span className="chat-platform-name">{row.platform}</span>
              {index === 0 && <span className="chat-platform-badge">Top Platform</span>}
            </div>
            <div className="chat-platform-meta">{row.count} {row.unit}</div>
          </div>
        ))}
      </div>
      <div className="chat-mini-chart">
        {sorted.map((row) => (
          <div key={`bar-${row.platform}`} className="chat-mini-chart-row">
            <span className="chat-mini-chart-label">{row.platform}</span>
            <div className="chat-mini-chart-track">
              <div className="chat-mini-chart-fill" style={{ width: `${Math.max(8, (row.count / max) * 100)}%` }} />
            </div>
            <span className="chat-mini-chart-value">{row.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderList(items, ordered) {
  // Platform cards only when all items are platform data
  const platformRows = items.map(parsePlatformRow).filter(Boolean);
  if (platformRows.length >= 2 && platformRows.length === items.length) {
    return renderPlatformCards(platformRows);
  }

  // Regular list — bullet or numbered
  if (ordered) {
    return (
      <ol className="chat-ol">
        {items.map((item, index) => (
          <li key={`ol-${index}`} className="chat-li">
            <span className="chat-li-num">{index + 1}</span>
            <span className="chat-li-text">{parseInline(item)}</span>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <ul className="chat-ul">
      {items.map((item, index) => (
        <li key={`ul-${index}`} className="chat-li">
          <span className="chat-li-dot" />
          <span className="chat-li-text">{parseInline(item)}</span>
        </li>
      ))}
    </ul>
  );
}

const ChatResponseRenderer = ({ text, role }) => {
  if (!text) return null;

  if (role === "user") {
    return <div className="chat-rich-text">{text}</div>;
  }

  const blocks = groupBlocks(text);

  return (
    <div className="chat-rich-text">
      {blocks.map((block, index) => {
        if (block.type === "list") {
          return (
            <div key={`list-${index}`}>
              {renderList(block.items, block.ordered)}
            </div>
          );
        }
        return (
          <p key={`p-${index}`} className="chat-paragraph">
            {parseInline(block.text)}
          </p>
        );
      })}
    </div>
  );
};

export default ChatResponseRenderer;